import Coverage from '@root/auto-pricing/src/models/coverage';
import Money from '@root/core/src/models/money';

// All six dimensions that identify a unique NJ PIP coverage entry.
const PIP_FIELDS = ['perPerson', 'deductible', 'perDay', 'perOccurrence'];
const PIP_OPTION_FIELDS = ['enhanced', 'residentRelativesIncluded'];

// Returns true if `selection` matches `coverage` on all dimensions except those in `except`.
export function pipPeerMatches(selection, coverage, except = []) {
  for (const field of PIP_FIELDS) {
    if (!except.includes(field) && selection[field] !== coverage[field]) { return false; }
  }
  for (const field of PIP_OPTION_FIELDS) {
    if (!except.includes(field) && selection.coverageOptions[field] !== coverage.coverageOptions[field]) { return false; }
  }
  return true;
}

// Finds the best peer in `selections` matching all dimensions of `coverage` except those in
// `except`. Falls back through `fallbackExcept` lists if no exact match exists.
export function findPipPeer(selections, coverage, except, fallbackExcept = []) {
  return selections.find((s) => pipPeerMatches(s, coverage, except)) ??
    (fallbackExcept.length
      ? selections.find((s) => pipPeerMatches(s, coverage, [...except, ...fallbackExcept]))
      : undefined);
}

export function hasExtraPip(coverage) {
  return coverage.perDay !== null && coverage.perDay !== undefined;
}

export function extraPipLabelKey(coverage) {
  const perDay = coverage.perDay ?? 'null';
  const perOccurrence = coverage.perOccurrence ?? 'null';
  return `${perDay}:${perOccurrence}`;
}

// Essential service amounts are NJ regulatory constants, not returned by the server.
// Base ($100/wk·$5,200) and Option 1 ($100/wk·$10,400) use $12/day; all others use $20/day.
const ESSENTIAL_SERVICE_AMOUNTS = {
  '100:5200': '$12/day · $4,380 total',
  '100:10400': '$12/day · $8,760 total',
};
const ENHANCED_ESSENTIAL_SERVICE = '$20/day · $14,600 total';

export function getExtraPipInfo(coverage) {
  if (!hasExtraPip(coverage)) { return null; }
  const key = extraPipLabelKey(coverage);
  const isBase = key === '100:5200';
  return {
    essentialService: ESSENTIAL_SERVICE_AMOUNTS[key] ?? ENHANCED_ESSENTIAL_SERVICE,
    deathBenefit: isBase
      ? 'Any unpaid remainder of the above (if applicable)'
      : Money.fromDollars(10000).formattedDollars(),
    funeralExpenses: isBase
      ? Money.fromDollars(1000).formattedDollars()
      : Money.fromDollars(2000).formattedDollars(),
  };
}

// Maps the NJ PIP `enhanced` coverage option to its primary-insurer display label.
// Per the NJ policy declaration contract (OBX-2592): enhanced='Y' means the insured's own health
// insurance is the primary PIP payer, and 'N' means Root is. This is the canonical mapping — other
// render sites should call this rather than re-deriving the ternary.
export function getPrimaryInsurer(coverage) {
  return coverage.coverageOptions[Coverage.CoverageOptions.ENHANCED] === Coverage.CoverageOptionChoice.YES
    ? 'My own health insurance'
    : 'Root';
}
